import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Mail, Lock, Eye, EyeOff, BarChart3, Shield, Sparkles, ArrowRight,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import AuthLayout from '../components/auth/AuthLayout';
import { toast } from 'sonner';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_8e7674db-cef4-4e13-8fa2-d45d710e0c70/artifacts/3k0mitlt_3E134DAB-3BC0-434D-9EE3-CBAFF63A0A46.png';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Connexion reussie');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      accentColor="#D4A745"
      accentLabel="Portail Agence"
      title="Pilotez votre agence en toute serenite"
      tagline="Tableau de bord unifie pour vos biens, clients, transactions, RH et finances — adapte aux exigences du marche malien."
      benefits={[
        { icon: BarChart3, label: 'Visibilite totale sur les loyers, retards et avances en un clin d\'oeil' },
        { icon: Shield, label: 'Multi-tenant, isolation par agence et audit complet des actions' },
        { icon: Sparkles, label: 'Generation PDF, signatures, notifications push et bien plus' },
      ]}
      agencyLogoUrl={LOGO_URL}
      otherPortals={[
        { to: '/tenant/login', label: 'Espace Locataire', sub: 'Quittances, paiements...' },
        { to: '/owner/login', label: 'Espace Proprietaire', sub: 'Soldes, retraits...' },
      ]}
    >
      {/* Heading */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-[#1B3A4B]">Connexion Admin / Agent</h2>
        <p className="text-sm text-gray-500 mt-1">Acceder a votre espace de gestion</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
        <div className="space-y-2">
          <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-gray-600">Email</Label>
          <div className="relative group">
            <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 group-focus-within:text-[#D4A745] transition-colors" />
            <Input
              id="email"
              type="email"
              autoComplete="email"
              placeholder="prenom@agence.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="pl-11 h-12 bg-gray-50 border-gray-200 focus:bg-white focus:border-[#D4A745] focus:ring-1 focus:ring-[#D4A745]"
              data-testid="login-email-input"
            />
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-gray-600">Mot de passe</Label>
          </div>
          <div className="relative group">
            <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 group-focus-within:text-[#D4A745] transition-colors" />
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pl-11 pr-11 h-12 bg-gray-50 border-gray-200 focus:bg-white focus:border-[#D4A745] focus:ring-1 focus:ring-[#D4A745]"
              data-testid="login-password-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#1B3A4B] transition-colors"
              data-testid="toggle-password-btn"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <Button
          type="submit"
          className="w-full h-12 bg-[#1B3A4B] hover:bg-[#142D3A] text-white font-semibold tracking-wide group relative overflow-hidden"
          disabled={loading}
          data-testid="login-submit-btn"
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Connexion...
            </span>
          ) : (
            <span className="flex items-center justify-center gap-2">
              Se connecter
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </span>
          )}
          <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#D4A745]" />
        </Button>
      </form>

      <div className="mt-6 flex items-center justify-between text-sm">
        <span className="text-gray-500">Pas encore inscrit ?</span>
        <div className="flex items-center gap-3">
          <Link
            to="/register"
            className="text-[#1B3A4B] hover:text-[#D4A745] font-medium transition-colors"
            data-testid="register-link"
          >
            Creer un compte
          </Link>
          <span className="text-gray-300">•</span>
          <Link
            to="/agency-register"
            className="text-[#1B3A4B] hover:text-[#D4A745] font-medium transition-colors"
            data-testid="agency-register-link"
          >
            Inscrire mon agence
          </Link>
        </div>
      </div>
    </AuthLayout>
  );
};

export default Login;
